import { PAYMENT_METHODS } from '@/constants/payment';
import { GtmEvents } from '@/gtm/gtm-events';
import { logger } from '@/plugins/logger';

export const deliveryTypes = {
  store: 'Магазин',
  pvz: 'ПВЗ',
  postamat: 'Постамат',
  courier: 'Курьер',
  post: 'Почта',
};

export const paymentMethods = {
  [PAYMENT_METHODS.cardOnline]: 'Онлайн',
  [PAYMENT_METHODS.online]: 'Онлайн',
  [PAYMENT_METHODS.fps]: 'СБП',
  [PAYMENT_METHODS.cashOnly]: 'ПриПолучении',
  [PAYMENT_METHODS.cashOrCard]: 'ПриПолучении',
  [PAYMENT_METHODS.installmentSovcombank]: 'BNPL',
  [PAYMENT_METHODS.sberbank]: 'Онлайн',
  [PAYMENT_METHODS.sovсombank]: 'BNPL',
};

const discountTypes = {
  promocode: 'Промокод',
  bonus: 'Бонус',
  promotion: 'Акция',
  ware_discount: 'Акция',
};

export class CartGtmEvents extends GtmEvents {
  constructor() {
    super('СобытияВКорзине');
  }

  viewPage({
    cartId, authEd, cartRevenue, cartQuantity, products = [],
  } = {}) {
    const eventLabel = JSON.stringify({
      cart_id: cartId,
      auth_ed: authEd ? 1 : 0,
      cart_revenue: cartRevenue,
      cart_quantity: cartQuantity,
      products: products.map(({ quantity, totalPrice, models: [model] }) => ({
        id: model?.code,
        price: totalPrice,
        // TODO: Сделать выход при отсутствии модели?
        // eslint-disable-next-line no-unsafe-optional-chaining
        priceRRP: model?.price?.basePrice * quantity,
        quantity,
      })),
    });

    return this.gtmEvent({
      eventAction: 'КРЗ_ПросмотрСтраницы',
      eventLabel,
    });
  }

  errorNotificationShown(errorText = '') {
    return this.gtmEvent({
      eventAction: 'КРЗ_Ошибка',
      eventLabel: errorText,
    });
  }

  viewPageNotEmptyCart() {
    return this.gtmEvent({
      eventAction: 'КРЗ_КорзинаНеПуста_ПросмотрСтраницы',
    });
  }

  loginModalShownOnCheckout() {
    return this.gtmEvent({
      eventAction: 'КРЗ_Авторизация_ПоказЭкрана',
    });
  }

  successLogin() {
    return this.gtmEvent({
      eventAction: 'КРЗ_Авторизация_Успешная',
    });
  }

  shippingGroupCollides(shippingGroups = []) {
    const COEFFICIENTS = {
      unallocated: 0.7,
      soldout: 0.7,
      pickup: 1,
      delivery: 1,
      thirdparty: 1,
    };
    const coefficient = shippingGroups.reduce((acc, { type }) => {
      // eslint-disable-next-line no-param-reassign
      acc += COEFFICIENTS[type] || 0;

      return acc;
    }, 0);

    const eventLabel = shippingGroups.map(({ type }) => type).join();

    if (coefficient > 1) {
      return this.gtmEvent({
        eventAction: 'КРЗ_Сложность_Столкновение',
        eventLabel,
      });
    }
  }

  viewCartPage({ cartId, products }) {
    const eventLabel = {
      cart_id: cartId,
      products: products.map(({ quantity, models: [model] }) => ({
        id: model?.code,
        price: model?.price?.discountPrice,
        quantity,
      })),
    };

    const checkoutProducts = products.map(({ quantity, skuId, models: [model] }) => ({
      dimension11: skuId,
      price: model?.price?.discountPrice,
      quantity,
    }));

    return this.gtmEvent({
      eventAction: 'Корзина_ПросмотрСтраницы',
      eventLabel: JSON.stringify(eventLabel),
      params: {
        ecommerce: {
          checkout: {
            products: checkoutProducts,
          },
        },
      },
    });
  }

  notChooseDeliveryType({ value } = {}) {
    return this.gtmEvent({
      eventAction: 'Корзина_НеВыбранСпособДоставки',
      eventLabel: value,
    });
  }

  submitOrder() {
    return this.gtmEvent({
      eventAction: 'Корзина_КнопкаОформитьЗаказ',
    });
  }

  shippingByPickup() {
    return this.gtmEvent({
      eventAction: 'Корзина_СпособДоставки_Самовывоз',
    });
  }

  shippingByPickupStore() {
    return this.gtmEvent({
      eventAction: 'Корзина_СпособДоставки_Самовывоз_КнопкаВыбратьЭтотПункт',
    });
  }

  shippingByCourier() {
    return this.gtmEvent({
      eventAction: 'Корзина_СпособДоставки_КурьерскаяСлужба',
    });
  }

  paymentMethodPreselected({ method }) {
    return this.gtmEvent({
      eventAction: 'КРЗ_СпособОплаты_Предвыбран',
      eventLabel: method?.title,
    });
  }

  paymentMethodChanged({ selectedMethod, availableMethods = [] }) {
    const eventLabel = JSON.stringify({
      НовыйСпособОплаты: availableMethods.find(({ value }) => value === selectedMethod)?.title,
      ДоступныеСпособы: availableMethods.map(({ title }) => title),
    });

    return this.gtmEvent({
      eventAction: 'КРЗ_СпособОплаты_Изменен',
      eventLabel,
    });
  }

  successSubmitOrder({
    cartId, orders, subtotal, discounts = [], preselectedPaymentMethod = '',
  }) {
    let eventLabel = '';
    try {
      let appliedDiscounts = discounts.reduce((res, { type }) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        discountTypes[type] && res.add(discountTypes[type]);
        return res;
      }, new Set());

      appliedDiscounts = [...appliedDiscounts].join('|');

      eventLabel = JSON.stringify({
        НомерКорзины: cartId,
        'Количество заказов': orders.length,
        СтоимостьКорзины: subtotal,
        ТипКорзины: orders.length === 1 ? 'Монокорзина' : 'Мультикорзина',
        Заказы: orders.map((order) => ({
          НомерЗаказа: order.orderNumber,
          СуммаЗаказа: order.subtotal,
          СпособДоставки: order.shippingGroupType === 'delivery'
            ? deliveryTypes.courier
            : deliveryTypes[order.storeType],
          СпособОплаты: paymentMethods[preselectedPaymentMethod || order.paymentMethod],
          НужнаПредоплата: order.needPrepay ? 'Да' : 'Нет',
          СкидочныеМеханики: appliedDiscounts,
        })),
      });
    } catch (e) {
      logger.error(e);
    }

    return this.gtmEvent({
      eventAction: 'КРЗ_ОформленЗаказ_Успешно',
      eventLabel,
    });
  }

  visibleDifficult({ group = {}, shippingGroups = [] }) {
    const deliveryNames = {
      delivery: 'Курьер',
      pickup: 'Самовывоз',
      postamat: 'Постамат',
      pvz: 'ПВЗ',
      unallocated: 'Не определена',
    };

    const getDeliveryNames = () => shippingGroups.map((g) => deliveryNames[g]);

    const eventLabel = JSON.stringify({
      id: group.type,
      ТипСложности: getDeliveryNames(),
    });

    return this.gtmEvent({
      eventAction: 'КРЗ_МСП_Сложность_Столкновение',
      eventLabel,
    });
  }

  shippingGroupEdited() {
    return this.gtmEvent({
      eventAction: 'КРЗ_SG_Редактировать',
    });
  }

  productsInCartVisibility({ visible }) {
    const eventAction = visible
      ? 'КРЗ_SG_Показать' : 'КРЗ_SG_Скрыть';

    return this.gtmEvent({
      eventAction,
    });
  }

  viewPageEmptyCart() {
    return this.gtmEvent({
      eventAction: 'КРЗ_КорзинаПуста_ПросмотрСтраницы',
    });
  }

  onClickStartShoppingBtn() {
    return this.gtmEvent({
      eventAction: 'КРЗ_КорзинаПуста_НачатьПокупкиКлик',
    });
  }
}

export const cartGtmEvents = new CartGtmEvents();
